import React from "react";
import { useTranslation } from "react-i18next";
import { Clock, XCircle, AlertCircle, Info } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatTime = (time) => (time ? time.substring(0, 5) : "-");

const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const getDayName = (date, t) => {
  const names = dayKeys.map((day) => t(`planning.calendarView.daysShort.${day}`));
  const i = date.getDay();
  return names[i === 0 ? 6 : i - 1];
};

const getDayClass = (day) => {
  if (day.is_outside_range) return "bg-muted/50 opacity-30 cursor-default";
  const dow = new Date(day.date).getDay();
  const isWeekend = dow === 0 || dow === 6;
  if (!day.is_working_day) return isWeekend ? "bg-muted/40 text-muted-foreground" : "bg-muted text-muted-foreground";
  if (day.is_override) return "bg-amber-950/20 border-amber-900/40";
  if (isWeekend) return "bg-muted/30";
  return "bg-card";
};

const getCalendarGrid = (days) => {
  if (!days || days.length === 0) return [];
  const dayMap = new Map();
  days.forEach((d) => dayMap.set(d.date, d));

  const firstDate = new Date(days[0].date);
  const lastDate = new Date(days[days.length - 1].date);

  const startDate = new Date(firstDate);
  const fdow = startDate.getDay();
  startDate.setDate(startDate.getDate() + (fdow === 0 ? -6 : 1 - fdow));

  const endDate = new Date(lastDate);
  const ldow = endDate.getDay();
  endDate.setDate(endDate.getDate() + (ldow === 0 ? 0 : 7 - ldow));

  const grid = [];
  const cur = new Date(startDate);
  while (cur <= endDate) {
    const dateStr = formatDate(cur);
    grid.push(dayMap.get(dateStr) || { date: dateStr, is_working_day: false, is_outside_range: true });
    cur.setDate(cur.getDate() + 1);
  }
  return grid;
};

const DayPopoverContent = ({ day, dayDate, dayName, t }) => (
  <PopoverContent className="w-80">
    <div className="space-y-3">
      <div>
        <h4 className="font-semibold">
          {dayName} {dayDate.getDate()}/{dayDate.getMonth() + 1}/{dayDate.getFullYear()}
        </h4>
        <p className="text-sm text-muted-foreground">{day.schedule_name || t("planning.calendarView.noSchedule")}</p>
      </div>
      {day.is_working_day ? (
        <>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t("planning.calendarView.start")}</span>
              <span className="font-medium">{formatTime(day.start_time)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t("planning.calendarView.end")}</span>
              <span className="font-medium">{formatTime(day.end_time)}</span>
            </div>
            {day.break_start && day.break_end && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("planning.calendarView.break")}</span>
                <span className="font-medium">{formatTime(day.break_start)} - {formatTime(day.break_end)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t("planning.calendarView.expectedHoursLabel")}</span>
              <span className="font-medium">{day.hours_expected}h</span>
            </div>
          </div>
          {day.is_override && (
            <div className="flex items-start gap-2 p-2 bg-amber-950/20 border border-amber-900/40 rounded">
              <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{t("planning.calendarView.exceptionalSchedule")}</p>
                {day.override_reason && (
                  <p className="text-xs text-muted-foreground mt-1">{day.override_reason}</p>
                )}
              </div>
            </div>
          )}
          {day.cycle_week_number && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Info className="h-4 w-4" />
              <span>{t("planning.calendarView.cycleWeek", { week: day.cycle_week_number })}</span>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-4">
          <XCircle className="h-8 w-8 mx-auto text-gray-400" />
          <p className="text-sm text-muted-foreground mt-2">{t("planning.calendarView.restDay")}</p>
        </div>
      )}
    </div>
  </PopoverContent>
);

const CalendarDayGrid = ({ scheduleData }) => {
  const { t } = useTranslation();
  const grid = getCalendarGrid(scheduleData.days);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-7 gap-2">
        {dayKeys.map((day) => (
          <div key={day} className="text-center font-semibold text-sm text-foreground py-2">
            {t(`planning.calendarView.daysFull.${day}`)}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {grid.map((day, index) => {
          const dayDate = new Date(day.date);
          const dayName = getDayName(dayDate, t);

          if (day.is_outside_range) {
            return (
              <Card key={index} className={getDayClass(day)}>
                <CardContent className="p-3"><div className="h-20" /></CardContent>
              </Card>
            );
          }

          return (
            <Popover key={index}>
              <PopoverTrigger asChild>
                <Card className={`cursor-pointer transition-all hover:shadow-md ${getDayClass(day)}`}>
                  <CardContent className="p-3">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">{dayName}</span>
                        <span className="text-xs font-bold">{dayDate.getDate()}</span>
                      </div>
                      {day.is_working_day ? (
                        <>
                          <div className="text-xs space-y-1">
                            <div className="flex items-center justify-center">
                              <div className="text-white px-2 py-1 rounded-md font-medium" style={{ backgroundColor: "#5e6ad2" }}>
                                <div className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  <span>{formatTime(day.start_time)} - {formatTime(day.end_time)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-center text-muted-foreground font-medium">{day.hours_expected}h</div>
                          </div>
                          {day.is_override && (
                            <Badge variant="outline" className="text-xs">{t("planning.calendarView.exception")}</Badge>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-2">
                          <XCircle className="h-4 w-4 mx-auto text-gray-400" />
                          <p className="text-xs text-muted-foreground mt-1">{t("planning.calendarView.rest")}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </PopoverTrigger>
              <DayPopoverContent day={day} dayDate={dayDate} dayName={dayName} t={t} />
            </Popover>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarDayGrid;
